/* =====================================================================
 * DocxParagraph — 단락 렌더링 (정렬, 들여쓰기, 리스트, 서식)
 * docDefaults → style 상속 → 단락 속성 → 런 속성 순서로 적용
 * ===================================================================== */

import React from 'react';
import type {
  DocxParagraph as DocxParagraphType,
  DocxDocument,
  DocxStyle,
} from '@auw/workspace-utils';
import { halfPointsToPx } from '@auw/workspace-utils';
import { DocxRunComponent } from './DocxRun';

interface DocxParagraphComponentProps {
  paragraph: DocxParagraphType;
  document: DocxDocument;
  inTable?: boolean;
}

/** 스타일 상속 해석 (basedOn 체인 추적) */
function resolveStyle(
  styleId: string | undefined,
  styles: Map<string, DocxStyle>,
  maxDepth = 10,
): Partial<DocxStyle> {
  if (!styleId || maxDepth <= 0) return {};
  const style = styles.get(styleId);
  if (!style) return {};

  const parentProps = style.basedOn ? resolveStyle(style.basedOn, styles, maxDepth - 1) : {};
  return { ...parentProps, ...style };
}

/* ------------------------------------------------------------------ */
/* 번호매기기 포맷 → 접두 문자열                                         */
/* ------------------------------------------------------------------ */

/** ①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳ */
const ENCLOSED_CIRCLES = '①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳';

function toRoman(num: number): string {
  const vals = [1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1];
  const syms = ['M', 'CM', 'D', 'CD', 'C', 'XC', 'L', 'XL', 'X', 'IX', 'V', 'IV', 'I'];
  let result = '';
  for (let i = 0; i < vals.length; i++) {
    while (num >= vals[i]) {
      result += syms[i];
      num -= vals[i];
    }
  }
  return result;
}

function getListPrefix(
  format: string,
  lvlText: string,
  index: number, // 1-based (already includes start offset from parser)
): string {
  let numStr: string;

  switch (format) {
    case 'bullet':
      return '\u2022 ';
    case 'decimal':
      numStr = String(index);
      break;
    case 'decimalEnclosedCircle':
      if (index >= 1 && index <= 20) {
        return ENCLOSED_CIRCLES[index - 1] + ' ';
      }
      numStr = String(index);
      break;
    case 'lowerLetter':
      numStr = String.fromCharCode(96 + ((index - 1) % 26) + 1);
      break;
    case 'upperLetter':
      numStr = String.fromCharCode(64 + ((index - 1) % 26) + 1);
      break;
    case 'lowerRoman':
      numStr = toRoman(index).toLowerCase();
      break;
    case 'upperRoman':
      numStr = toRoman(index);
      break;
    case 'koreanCounting':
    case 'korean': {
      const korNums = ['일', '이', '삼', '사', '오', '육', '칠', '팔', '구', '십'];
      numStr = index <= 10 ? korNums[index - 1] : String(index);
      break;
    }
    case 'ganada': {
      const ganada = 'ㄱㄴㄷㄹㅁㅂㅅㅇㅈㅊㅋㅌㅍㅎ';
      numStr = index <= ganada.length ? ganada[index - 1] : String(index);
      break;
    }
    default:
      numStr = String(index);
      break;
  }

  // lvlText 패턴 적용 (e.g. "%1.", "%1)", "%1")
  if (lvlText) {
    return lvlText.replace(/%\d/g, numStr) + ' ';
  }
  return numStr + ' ';
}

/* ------------------------------------------------------------------ */
/* 줄 간격 계산                                                         */
/* ------------------------------------------------------------------ */

/**
 * OOXML line spacing → CSS line-height 변환
 *
 * OOXML의 lineRule="auto"에서 w:line=240은 "1줄 간격(single spacing)"을 의미한다.
 * 이는 폰트의 자연 줄 높이(winAscent + winDescent) 기준이며,
 * CSS의 line-height: 1.0 (= font-size 높이)과는 다르다.
 *
 * 한글 폰트(맑은 고딕) 메트릭 기준:
 *   winAscent=1100, winDescent=360, unitsPerEm=2048
 *   → single spacing = (1100+360)/2048 × 2 ≈ 1.43
 *
 * 한글+영문 혼합 문서에서 맑은 고딕이 줄 높이를 결정하므로 1.4 사용.
 */
const DEFAULT_SINGLE_SPACING_FACTOR = 1.4;

function getSingleSpacingFactor(fontFamily?: string): number {
  const family = (fontFamily || '').toLowerCase();
  // test_03 처럼 Arial Unicode MS 기반 문서는 Word 대비 브라우저에서 줄이 덜 감기므로
  // auto line-height 계수를 높여 페이지 분할을 맞춘다.
  if (family.includes('arial unicode')) return 1.6;
  if (family.includes('arial')) return 1.5;
  return DEFAULT_SINGLE_SPACING_FACTOR;
}

function getFontSizeScale(fontFamily?: string): number {
  const family = (fontFamily || '').toLowerCase();
  // Linux 환경에서 Arial Unicode MS 대체 폰트로 렌더될 때 Word보다 크게 보이는 보정
  if (family.includes('arial unicode')) return 0.90;
  return 1;
}

function computeLineHeight(
  line?: number,
  lineRule?: string,
  fontFamily?: string,
): number | undefined {
  if (!line) return undefined;

  if (lineRule === 'exact' || lineRule === 'atLeast') {
    // twips → px (1 twip = 1/20 pt, 1pt = 96/72 px)
    return (line / 20) * (96 / 72);
  }
  // auto: 240 = single spacing → CSS ~1.2, 360 = 1.5× → CSS ~1.8, 480 = 2.0× → CSS ~2.4
  return (line / 240) * getSingleSpacingFactor(fontFamily);
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const DocxParagraphComponent: React.FC<DocxParagraphComponentProps> = ({
  paragraph,
  document: doc,
  inTable = false,
}) => {
  const resolvedStyle = resolveStyle(paragraph.styleId, doc.styles);
  const defaults = doc.docDefaults;
  const resolvedFontFamily =
    paragraph.runProps?.fontFamily ||
    resolvedStyle.fontFamily ||
    defaults?.fontFamily;

  // CSS 스타일 구성 — docDefaults → style → paragraph 순서로 적용
  const style: React.CSSProperties = {
    margin: 0,
    minHeight: '1em',
    wordBreak: 'break-word',
  };

  // ── 1) 줄 간격 (docDefaults → style → paragraph) ──
  const defLineHeight = computeLineHeight(
    defaults?.spacing?.line,
    defaults?.spacing?.lineRule,
    resolvedFontFamily,
  );
  const styleLineHeight = computeLineHeight(
    resolvedStyle.spacing?.line,
    resolvedStyle.spacing?.lineRule,
    resolvedFontFamily,
  );
  const paraLineHeight = computeLineHeight(
    paragraph.spacing?.line,
    paragraph.spacing?.lineRule,
    resolvedFontFamily,
  );

  const finalLineHeight =
    paraLineHeight ??
    styleLineHeight ??
    defLineHeight ??
    getSingleSpacingFactor(resolvedFontFamily);

  if (typeof finalLineHeight === 'number') {
    // auto mode returns a ratio (e.g. 1.0, 1.08), exact/atLeast returns px
    if ((paragraph.spacing?.lineRule ?? resolvedStyle.spacing?.lineRule ?? defaults?.spacing?.lineRule ?? 'auto') === 'auto') {
      style.lineHeight = finalLineHeight;
    } else {
      style.lineHeight = `${finalLineHeight}px`;
    }
  }

  // ── 2) 정렬 (docDefaults → style → paragraph) ──
  const alignment = paragraph.alignment || resolvedStyle.alignment || defaults?.alignment;
  if (alignment) {
    const alignMap: Record<string, React.CSSProperties['textAlign']> = {
      left: 'left',
      center: 'center',
      right: 'right',
      both: 'justify',
      distribute: 'justify',
    };
    style.textAlign = alignMap[alignment] || 'left';
  }

  // ── 3) 간격 before/after (docDefaults → style → paragraph) ──
  const spacingBefore = paragraph.spacing?.before ?? resolvedStyle.spacing?.before ?? defaults?.spacing?.before;
  const spacingAfter = paragraph.spacing?.after ?? resolvedStyle.spacing?.after ?? defaults?.spacing?.after;
  if (spacingBefore) style.marginTop = `${spacingBefore}px`;
  if (spacingAfter) style.marginBottom = `${spacingAfter}px`;

  // ── 4) 들여쓰기 ──
  const indent = paragraph.indent || resolvedStyle.indent;
  if (indent) {
    if (indent.left) style.paddingLeft = `${indent.left}px`;
    if (indent.right) style.paddingRight = `${indent.right}px`;
    if (indent.firstLine) style.textIndent = `${indent.firstLine}px`;
    if (indent.hanging) {
      style.textIndent = `-${indent.hanging}px`;
      // hanging 시 paddingLeft가 없으면 hanging만큼 추가
      if (!indent.left) style.paddingLeft = `${indent.hanging}px`;
    }
  }

  // ── 5) 폰트 속성 (docDefaults → style → paragraph rPr) ──
  const fontSize = paragraph.runProps?.fontSize || resolvedStyle.fontSize || defaults?.fontSize;
  if (fontSize) {
    const sizePt = halfPointsToPx(fontSize) * (inTable ? getFontSizeScale(resolvedFontFamily) : 1);
    style.fontSize = `${sizePt}pt`;
  }
  if (resolvedFontFamily) style.fontFamily = resolvedFontFamily;
  if (paragraph.runProps?.color || resolvedStyle.color) style.color = paragraph.runProps?.color || resolvedStyle.color;
  if (paragraph.runProps?.bold || resolvedStyle.bold) style.fontWeight = 'bold';
  if (paragraph.runProps?.italic || resolvedStyle.italic) style.fontStyle = 'italic';

  // ── 6) 번호 매기기 ──
  let listPrefix: string | null = null;
  if (paragraph.numbering) {
    const numDef = doc.numbering.get(paragraph.numbering.numId);
    if (numDef) {
      const levelDef = numDef.levels.get(paragraph.numbering.level);
      if (levelDef) {
        listPrefix = getListPrefix(levelDef.format, levelDef.text, paragraph.numbering.index);

        // 직접 문단 들여쓰기가 최우선이고, numbering은 스타일보다 우선한다.
        if (levelDef.indent && paragraph.indent?.left === undefined) {
          style.paddingLeft = `${levelDef.indent}px`;
        }
        if (
          levelDef.hanging &&
          paragraph.indent?.firstLine === undefined &&
          paragraph.indent?.hanging === undefined
        ) {
          style.textIndent = `-${levelDef.hanging}px`;
          if (!levelDef.indent && paragraph.indent?.left === undefined) {
            style.paddingLeft = `${levelDef.hanging}px`;
          }
        }
      }
    }
  }

  // 빈 단락
  const isEmpty = paragraph.runs.length === 0 ||
    paragraph.runs.every(r => r.type === 'text' && !r.text);

  return (
    <div
      data-block-id={paragraph.id}
      data-block-type="paragraph"
      style={style}
    >
      {listPrefix && (
        <span
          style={{ userSelect: 'none', display: 'inline' }}
          contentEditable={false}
        >
          {listPrefix}
        </span>
      )}
      {isEmpty ? (
        <br />
      ) : (
        paragraph.runs.map((run, idx) => (
          <DocxRunComponent
            key={run.id}
            run={run}
            runIndex={idx}
            document={doc}
            inTable={inTable}
          />
        ))
      )}
    </div>
  );
};
